import { Typography } from '@breetometer/components';
import {
	clearCountDownStartTimeArray,
	selectCountDownStartTimeArray,
	setCountDownStartTimeArray,
	useAppDispatch,
	useAppSelector,
} from 'BreetRedux';
import { useCallback, useEffect, useRef, useState } from 'react';

import { COUNTDOWNCOLORS, getCountdownInitialState, type ICountdownTimer } from './CountDownTimer.helpers';

const CountdownTimer = ({
	duration,
	onComplete,
	colorChange = false,
	size = 200,
	format = 'mm:ss',
	strokeWidth = 8,
	start,
	persistCountDown,
	countDownId,
}: ICountdownTimer) => {
	const dispatch = useAppDispatch();
	const countDownStartTimeArray = useAppSelector(selectCountDownStartTimeArray);

	const countDownStartTime = countDownStartTimeArray.find((i) => i.id === countDownId);

	const saveCountdownStartTime = useCallback(
		(startTime: number) => {
			dispatch(setCountDownStartTimeArray(countDownStartTimeArray.map((i) => (i.id === countDownId ? { ...i, startTime } : i))));
		},
		[countDownStartTimeArray, dispatch, countDownId]
	);

	const initialState = getCountdownInitialState(countDownStartTime?.startTime, duration, !!start, !!persistCountDown);

	const [timeLeft, setTimeLeft] = useState(initialState.timeLeft);
	const [isRunning, setIsRunning] = useState(initialState.isRunning);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const isRunningRef = useRef(isRunning);

	useEffect(() => {
		isRunningRef.current = isRunning;
	}, [isRunning]);

	const timeLeftRef = useRef(timeLeft);

	useEffect(() => {
		timeLeftRef.current = timeLeft;
	}, [timeLeft]);

	useEffect(() => {
		if (isRunning) {
			intervalRef.current = setInterval(() => {
				setTimeLeft((prevTime) => {
					if (prevTime <= 1) {
						setIsRunning(false);

						if (onComplete) onComplete();

						if (persistCountDown) {
							dispatch(clearCountDownStartTimeArray());
						}
						return 0;
					}
					return prevTime - 1;
				});
			}, 1000);
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}

			if (persistCountDown && isRunningRef.current && timeLeftRef.current > 0) {
				saveCountdownStartTime(Date.now() + timeLeftRef.current * 1000);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, isRunning, onComplete, persistCountDown]);

	useEffect(() => {
		if (!persistCountDown) return;

		if (isRunning) {
			const newStartime = Date.now() + timeLeft * 1000;

			if (countDownStartTime?.startTime === undefined || Math.abs(countDownStartTime.startTime - newStartime) > 1000) {
				saveCountdownStartTime(newStartime);
			}
		}

		if (!isRunning && countDownStartTime?.startTime !== undefined) {
			dispatch(clearCountDownStartTimeArray());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countDownStartTime?.startTime, dispatch, isRunning, persistCountDown, timeLeft]);

	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;

	const formatTime = useCallback(
		(seconds: number): string => {
			const hrs = Math.floor(seconds / 3600);
			const mins = Math.floor((seconds % 3600) / 60);
			const secs = seconds % 60;

			switch (format) {
				case 'ss':
					return secs.toString().padStart(2, '0');
				case 'mm:ss':
					return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
				case 'hh:mm:ss':
					return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
				default:
					return secs.toString();
			}
		},
		[format]
	);

	const getColor = useCallback((): string => {
		if (!colorChange) return COUNTDOWNCOLORS.GREEN;

		const percentage = (timeLeft / duration) * 100;

		if (typeof colorChange === 'object') {
			if (percentage > 66) return colorChange.start;
			if (percentage > 33) return colorChange.middle;
			return colorChange.end;
		}

		if (percentage > 66) return COUNTDOWNCOLORS.GREEN;
		if (percentage > 33) return COUNTDOWNCOLORS.YELLOW;
		return COUNTDOWNCOLORS.RED;
	}, [timeLeft, duration, colorChange]);

	const strokeDashoffset = circumference - (timeLeft / duration) * circumference;

	return (
		<div
			className='countdown-timer'
			style={{ width: size, height: size }}
		>
			<svg
				width={size}
				height={size}
				className='transform -rotate-90'
			>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					stroke={COUNTDOWNCOLORS.GRAY}
					strokeWidth={strokeWidth}
					fill='none'
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					stroke={getColor()}
					strokeWidth={strokeWidth}
					fill='none'
					strokeDasharray={circumference}
					strokeDashoffset={strokeDashoffset}
					strokeLinecap='round'
					style={{
						transition: 'stroke-dashoffset 1s linear, stroke 0.3s ease',
					}}
				/>
			</svg>

			<div className='countdown-timer_timeleft'>
				<Typography
					as='h1'
					className='font-bold tabular-nums'
					style={{
						fontSize: size * 0.2,
						color: getColor(),
					}}
				>
					{formatTime(timeLeft)}
				</Typography>
			</div>
		</div>
	);
};

export default CountdownTimer;
